import React, { useState, useEffect } from "react";
import PendingGame from "./pendingGame";
import RunningGame from "./runningGame";
import io from "socket.io-client";
import { Text, View, StyleSheet } from "react-native";
import getEnvVars from "../../../../environment";
import Button from "../../forms/button";
import { Theme, attributes } from "../../theme";
import { graphql } from "react-apollo";
import gql from "graphql-tag";

const { websocketUrl } = getEnvVars();

const cleanup = ({ socket, interval }) => {
  socket.removeListener("game:send");
  socket.removeListener("game:join:request");
  socket.removeListener("action:send");
  clearInterval(interval);
};

const handleQuit = async ({
  id,
  name,
  quitGameRequest,
  setIsSubmitting,
  navigate,
}) => {
  try {
    setIsSubmitting(true);
    await quitGameRequest({
      variables: { id: id.toLowerCase(), name },
    });

    global.RunningGame = null;

    navigate("Home");
  } catch (e) {
    setIsSubmitting(false);
    console.log(e);
  }
};

const CurrentGame = (props) => {
  if (!global.RunningGame) {
    return;
  }

  const name = global.RunningGame.name;
  const gameId = global.RunningGame.game.id;

  const [game, setGame] = useState(null);
  const [action, setAction] = useState(null);
  const [interval, setGameInterval] = useState(null);
  const [socket, setSocket] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const socket = io(websocketUrl);
    setSocket(socket);

    const interval = setInterval(() => {
      socket.emit("game:heartbeat", { id: gameId });
    }, 10000);

    setGameInterval(interval);

    socket.removeListener("game:send");

    socket.on("game:send", (gotGame) => {
      setGame(gotGame);
      if (gotGame.currentAction) {
        setAction(gotGame.currentAction);
      }

      if (gotGame.status === "finished") {
        socket.close();
      }
    });

    socket.on("game:join:request", () => {
      socket.emit(`game:join`, { id: gameId });
    });

    if (!game) {
      socket.emit("game:join", { id: gameId });
    }

    return () => cleanup({ socket, interval });
  }, [gameId, name]);

  if (!game) {
    return (
      <Theme>
        <View style={styles.container}>
          <Text style={styles.connectingText}>Connecting to {gameId}...</Text>
        </View>
      </Theme>
    );
  }

  if (game.status === "pending") {
    return <PendingGame setGame={setGame} game={game} name={name} {...props} />;
  }

  if (game.status === "finished") {
    cleanup({ socket, interval });
    return (
      <Theme>
        <View style={styles.container}>
          <Text>Game {gameId} has finished. Thanks for playing!</Text>
        </View>
        <View style={styles.buttonContainer}>
          <Button
            raised={false}
            title="Quit"
            disabled={isSubmitting}
            buttonStyle={styles.button}
            titleStyle={styles.buttonText}
            onPress={() =>
              handleQuit({
                id: game.id,
                name,
                quitGameRequest: props.quitGame,
                setIsSubmitting,
                navigate: props.navigation.navigate,
              })
            }
          />
        </View>
      </Theme>
    );
  }

  return (
    <RunningGame
      action={action}
      game={game}
      gameId={gameId}
      name={name}
      handleQuit={handleQuit}
      {...props}
    />
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
  },
  connectingText: {
    fontWeight: "bold",
  },
  buttonContainer: {
    flex: 1,
    justifyContent: "flex-end",
    alignItems: "center",
    marginBottom: 25,
  },
  button: {
    backgroundColor: attributes.mainColor,
    width: 100,
  },
  buttonText: {
    color: attributes.backgroundColor,
    fontSize: 24,
  },
});

const QUIT_GAME_MUTATION = gql`
  mutation createJoinGameMutation($id: String!, $name: String!) {
    quitGame(id: $id, name: $name)
  }
`;

const CurrentGameWithMutation = graphql(QUIT_GAME_MUTATION, {
  name: "quitGame",
})(CurrentGame);

export default CurrentGameWithMutation;
